/**
 * Chat message pagination + optimistic-send helpers (assessment U8).
 *
 * The messages list endpoint is cursor-paginated: it returns the newest
 * `limit` messages (DESC by createdAt) plus `hasMore`, and accepts a
 * `before` ISO cursor to fetch the next older window. The web hook drives
 * that with TanStack `useInfiniteQuery`; these pure helpers hold the
 * cursor math, page flattening, and the optimistic-send reconcile so they
 * can be unit-tested without React.
 */
import type { ChatMessage, ChatMessageListResponse, ChatReactionSummary } from "@l30/shared";
import type { InfiniteData } from "@tanstack/react-query";

/** Prefix marking a not-yet-acknowledged optimistic message id. */
const OPTIMISTIC_ID_PREFIX = "optimistic-";

export type MessagePages = InfiniteData<ChatMessageListResponse>;

/**
 * Cursor for the next (older) page: the createdAt of the oldest message in
 * the last-loaded page, or `undefined` when the server says there is no
 * more history. Pass straight to `getNextPageParam`.
 *
 * Messages within a page are newest-first, so the oldest is the last one.
 */
export function nextBeforeCursor(lastPage: ChatMessageListResponse): string | undefined {
  if (!lastPage.hasMore) return undefined;
  const oldest = lastPage.messages[lastPage.messages.length - 1];
  return oldest?.createdAt;
}

/**
 * Collapse the infinite-query pages into the single `{ messages, reactions }`
 * shape the UI renders. Pages are ordered newest-batch first, and messages
 * within a page are newest-first, so the flattened array stays newest-first
 * overall (the view reverses it for bottom-anchored display).
 *
 * Deduped by id: a WebSocket/poll refetch can briefly overlap an optimistic
 * insert, and older-page windows can share a boundary row.
 */
export function flattenMessagePages(data: MessagePages | undefined): {
  messages: ChatMessage[];
  reactions: Record<string, ChatReactionSummary[]>;
} {
  const pages = data?.pages ?? [];
  const messages: ChatMessage[] = [];
  const seen = new Set<string>();
  const reactions: Record<string, ChatReactionSummary[]> = {};
  for (const page of pages) {
    for (const message of page.messages) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      messages.push(message);
    }
    Object.assign(reactions, page.reactions);
  }
  return { messages, reactions };
}

/** Build the placeholder message shown the instant a user hits Send. */
export function buildOptimisticMessage(input: {
  id: string;
  channelId: string;
  threadRootId: string | null;
  authorEmail: string;
  body: string;
  attachments: ChatMessage["attachments"];
  createdAt: string;
}): ChatMessage {
  return {
    id: input.id,
    channelId: input.channelId,
    threadRootId: input.threadRootId,
    authorEmail: input.authorEmail,
    kind: "user",
    body: input.body,
    attachments: input.attachments,
    mentions: [],
    editedAt: null,
    deletedAt: null,
    deletedBy: null,
    createdAt: input.createdAt,
  };
}

/**
 * Insert an optimistic message at the newest position (front of the first
 * page). If the cache is empty (channel not yet loaded), seed a single page
 * so the message still renders. Returns a new object graph — never mutates.
 */
export function insertOptimisticMessage(
  data: MessagePages | undefined,
  message: ChatMessage,
): MessagePages {
  if (!data || data.pages.length === 0) {
    return {
      pages: [{ messages: [message], reactions: {}, hasMore: false }],
      pageParams: [undefined],
    };
  }
  const [first, ...rest] = data.pages;
  if (!first) return data;
  return {
    ...data,
    pages: [{ ...first, messages: [message, ...first.messages] }, ...rest],
  };
}

/** True for ids minted by {@link buildOptimisticMessage} — i.e. unacknowledged sends. */
export function isOptimisticId(id: string): boolean {
  return id.startsWith(OPTIMISTIC_ID_PREFIX);
}
